import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropertySearch from '../../components/feature/PropertySearch';
import PropertyCompare from '../../components/feature/PropertyCompare';
import PropertyDetails from '../../components/feature/PropertyDetails';
import AppointmentBooking from '../../components/feature/AppointmentBooking';
import PropertyRequestForm from '../../components/feature/PropertyRequestForm';
import PropertyPDFRequest from '../../components/feature/PropertyPDFRequest';

interface Property {
  id: string;
  title: string;
  price: number;
  sale_price?: number;
  rent_price?: number;
  currency?: string;
  location: string;
  bedrooms: number;
  bathrooms: number;
  area: number;
  property_type: string;
  status: string;
  available_for_sale?: boolean;
  available_for_rent?: boolean;
  images: string[];
  description: string;
  features: string[];
  year_built?: number;
  parking?: number;
  floor?: number;
  furnished?: boolean;
  city: string;
  address?: string;
  videos?: string[];
  latitude?: number;
  longitude?: number;
  is_featured?: boolean;
  maintenance_fee?: number;
  has_maintenance_fee?: boolean;
  included_services?: string[];
}

interface ContactInfo {
  phones: string[];
  emails: string[];
  address: string;
  schedule: {
    weekdays: string;
    saturday: string;
    sunday: string;
  };
  map_url: string;
}

interface AboutUs {
  title: string;
  description: string;
  mission: string;
  vision: string;
  values: string[];
}

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const [properties, setProperties] = useState<Property[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([]);
  const [compareList, setCompareList] = useState<Property[]>([]);
  const [showCompare, setShowCompare] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [showAppointmentBooking, setShowAppointmentBooking] = useState(false);
  const [appointmentProperty, setAppointmentProperty] = useState<Property | null>(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestProperty, setRequestProperty] = useState<Property | null>(null);
  const [requestType, setRequestType] = useState<'compra' | 'renta'>('compra');
  const [showPDFRequest, setShowPDFRequest] = useState(false);
  const [pdfProperty, setPdfProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [aboutUs, setAboutUs] = useState<AboutUs | null>(null);

  useEffect(() => {
    fetchProperties();
    fetchSiteConfig();
  }, []);

  const fetchSiteConfig = async () => {
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      
      console.log('🔄 Fetching site configuration...');
      
      const response = await fetch(`${supabaseUrl}/functions/v1/site-config-api`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`
        }
      });
      
      const result = await response.json();
      console.log('📥 Site config response:', result);

      if (result.success) {
        if (result.data.contact_info) {
          console.log('✅ Contact info loaded:', result.data.contact_info);
          setContactInfo(result.data.contact_info);
        }
        if (result.data.about_us) {
          console.log('✅ About us loaded:', result.data.about_us);
          setAboutUs(result.data.about_us);
        }
      } else {
        console.error('❌ Error in site config response:', result.error);
      }
    } catch (error) {
      console.error('❌ Error fetching site config:', error);
    }
  };

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/properties-api`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ action: 'all' })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Properties data:', data);
        
        // Mapear los datos de la base de datos al formato esperado
        const mappedProperties = (data.properties || []).map((prop: any) => ({
          id: prop.id,
          title: prop.title || 'Propiedad sin título',
          price: parseFloat(prop.price) || 0,
          sale_price: prop.sale_price ? parseFloat(prop.sale_price) : null,
          rent_price: prop.rent_price ? parseFloat(prop.rent_price) : null,
          currency: prop.currency || 'MXN',
          location: prop.location || prop.city || 'Ubicación no especificada',
          bedrooms: prop.bedrooms || 0,
          bathrooms: prop.bathrooms || 0,
          area: parseFloat(prop.area) || 0,
          property_type: prop.property_type || 'casa',
          status: prop.status || 'available',
          available_for_sale: prop.available_for_sale || false,
          available_for_rent: prop.available_for_rent || false,
          images: Array.isArray(prop.images) ? prop.images : (prop.images ? [prop.images] : []),
          description: prop.description || 'Sin descripción disponible',
          features: Array.isArray(prop.features) ? prop.features : (typeof prop.features === 'object' ? Object.keys(prop.features || {}) : []),
          year_built: prop.year_built,
          parking: prop.parking,
          floor: prop.floor,
          furnished: prop.furnished,
          city: prop.city || '',
          address: prop.address,
          videos: Array.isArray(prop.videos) ? prop.videos : [],
          latitude: prop.latitude,
          longitude: prop.longitude,
          is_featured: prop.is_featured || false,
          maintenance_fee: prop.maintenance_fee ? parseFloat(prop.maintenance_fee) : null,
          has_maintenance_fee: prop.has_maintenance_fee || false,
          included_services: Array.isArray(prop.included_services) ? prop.included_services : []
        }));
        
        setProperties(mappedProperties);
        setFilteredProperties(mappedProperties);
      } else {
        console.error('Error fetching properties:', response.statusText);
        // Si hay error, mostrar array vacío en lugar de mock data
        setProperties([]);
        setFilteredProperties([]);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
      // Si hay error, mostrar array vacío en lugar de mock data
      setProperties([]);
      setFilteredProperties([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (filters: any) => {
    let filtered = properties;

    if (filters.type) {
      filtered = filtered.filter(p => p.property_type === filters.type);
    }
    if (filters.status) {
      // Mapear el status del filtro al status de la base de datos
      const statusMap: { [key: string]: string } = {
        'venta': 'available',
        'alquiler': 'available', // Asumiendo que ambos son available
        'available': 'available',
        'sold': 'sold',
        'rented': 'rented'
      };
      const mappedStatus = statusMap[filters.status] || filters.status;
      filtered = filtered.filter(p => p.status === mappedStatus);
    }
    if (filters.location) {
      filtered = filtered.filter(p => 
        p.location.toLowerCase().includes(filters.location.toLowerCase()) ||
        p.city.toLowerCase().includes(filters.location.toLowerCase())
      );
    }
    if (filters.minPrice) {
      filtered = filtered.filter(p => p.price >= Number(filters.minPrice));
    }
    if (filters.maxPrice) {
      filtered = filtered.filter(p => p.price <= Number(filters.maxPrice));
    }
    if (filters.bedrooms) {
      filtered = filtered.filter(p => p.bedrooms >= Number(filters.bedrooms));
    }
    if (filters.bathrooms) {
      filtered = filtered.filter(p => p.bathrooms >= Number(filters.bathrooms));
    }
    if (filters.area) {
      filtered = filtered.filter(p => p.area >= Number(filters.area));
    }

    setFilteredProperties(filtered);
  };

  const handleResetSearch = () => {
    setFilteredProperties(properties);
  };

  const toggleCompare = (property: Property) => {
    setCompareList(prev => {
      const exists = prev.find(p => p.id === property.id);
      if (exists) {
        return prev.filter(p => p.id !== property.id);
      } else {
        if (prev.length >= 3) {
          return [...prev.slice(1), property]; // Max 3 properties
        }
        return [...prev, property];
      }
    });
  };

  const removeFromCompare = (propertyId: string) => {
    setCompareList(prev => prev.filter(p => p.id !== propertyId));
  };

  const formatPrice = (price: number, currency: string = 'MXN') => {
    if (currency === 'USD') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(price);
    } else {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(price);
    }
  };

  const formatPropertyPrice = (property: Property) => {
    const currency = property.currency || 'MXN';
    
    if (property.available_for_sale && property.available_for_rent) {
      // Ambos precios disponibles
      return (
        <div className="space-y-1">
          <div className="text-lg font-bold text-green-600">
            <i className="ri-price-tag-3-line mr-1"></i>
            Venta: {formatPrice(property.sale_price || 0, currency)}
          </div>
          <div className="text-lg font-bold text-blue-600">
            <i className="ri-calendar-line mr-1"></i>
            Renta: {formatPrice(property.rent_price || 0, currency)}/mes
          </div>
        </div>
      );
    } else if (property.available_for_sale) {
      // Solo venta
      return (
        <div className="text-xl font-bold text-green-600">
          <i className="ri-price-tag-3-line mr-1"></i>
          {formatPrice(property.sale_price || property.price || 0, currency)}
        </div>
      );
    } else if (property.available_for_rent) {
      // Solo renta
      return (
        <div className="text-xl font-bold text-blue-600">
          <i className="ri-calendar-line mr-1"></i>
          {formatPrice(property.rent_price || property.price || 0, currency)}/mes
        </div>
      );
    } else {
      // Precio legacy
      return (
        <div className="text-xl font-bold text-blue-600">
          {formatPrice(property.price, currency)}
        </div>
      );
    }
  };

  const getAvailabilityBadges = (property: Property) => {
    const badges = [];
    
    if (property.available_for_sale) {
      badges.push({
        text: 'Venta',
        color: 'bg-green-100 text-green-800',
        icon: 'ri-price-tag-3-line'
      });
    }
    
    if (property.available_for_rent) {
      badges.push({
        text: 'Renta',
        color: 'bg-blue-100 text-blue-800',
        icon: 'ri-calendar-line'
      });
    }
    
    // Fallback para propiedades legacy
    if (badges.length === 0) {
      const legacyText = property.status === 'rented' ? 'Renta' : 'Venta';
      const legacyColor = property.status === 'rented' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800';
      const legacyIcon = property.status === 'rented' ? 'ri-calendar-line' : 'ri-price-tag-3-line';
      
      badges.push({
        text: legacyText,
        color: legacyColor,
        icon: legacyIcon
      });
    }
    
    return badges;
  };

  const getServiceIcons = (services: string[]) => {
    const serviceMap: { [key: string]: { icon: string; label: string; color: string } } = {
      'internet': { icon: 'ri-wifi-line', label: 'Internet', color: 'text-blue-600' },
      'mantenimiento': { icon: 'ri-tools-line', label: 'Mantenimiento', color: 'text-purple-600' },
      'agua': { icon: 'ri-drop-line', label: 'Agua', color: 'text-cyan-600' },
      'gas': { icon: 'ri-fire-line', label: 'Gas', color: 'text-orange-600' }
    };
    
    return services.map(service => serviceMap[service]).filter(Boolean);
  };

  const getPropertyTypeLabel = (type: string) => {
    const typeMap: { [key: string]: string } = {
      'casa': 'Casa',
      'oficina': 'Oficina',
      'local_comercial': 'Local Comercial',
      'terreno': 'Terreno',
      'apartamento': 'Apartamento'
    };
    return typeMap[type] || type;
  };

  const getStatusLabel = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'available': t('forSale'),
      'sold': 'Vendida',
      'rented': t('forRent'),
      'pending': 'Pendiente'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colorMap: { [key: string]: string } = {
      'available': 'bg-green-100 text-green-800',
      'sold': 'bg-red-100 text-red-800',
      'rented': 'bg-blue-100 text-blue-800',
      'pending': 'bg-yellow-100 text-yellow-800'
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const handlePropertyClick = (property: Property) => {
    setSelectedProperty(property);
  };

  const handleBookVisit = (property: Property) => {
    setAppointmentProperty(property);
    setSelectedProperty(null);
    setShowAppointmentBooking(true);
  };

  const handlePropertyRequest = (property: Property, type: 'compra' | 'renta') => {
    setRequestProperty(property);
    setRequestType(type);
    setShowRequestForm(true);
  };

  const handlePDFRequest = (property: Property) => {
    setPdfProperty(property);
    setShowPDFRequest(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando propiedades...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/05c782bef5e03af806ba1d51091d6c47/3aa9c4448526bbcc8377a061d058f682.png" 
                alt="GRUPO FR" 
                className="h-16 w-auto object-contain"
              />
            </div>

            <nav className="hidden md:flex items-center space-x-8">
              <a href="#propiedades" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">
                {t('featuredProperties')}
              </a>
              <a href="#nosotros" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">
                {t('aboutUs')}
              </a>
              <a href="#contacto" className="text-gray-600 hover:text-blue-600 transition-colors cursor-pointer">
                {t('contactUs')}
              </a>
            </nav>

            <div className="flex items-center gap-4">
              {/* Language Selector */}
              <div className="relative">
                <select
                  value={i18n.language}
                  onChange={(e) => changeLanguage(e.target.value)}
                  className="appearance-none bg-white border border-gray-200 rounded-lg px-3 py-2 pr-8 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                >
                  <option value="es">🇪🇸 ES</option>
                  <option value="en">🇺🇸 EN</option>
                  <option value="fr">🇫🇷 FR</option>
                  <option value="pt">🇧🇷 PT</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <i className="ri-global-line text-gray-400"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat py-32"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.3)), url('https://public.readdy.ai/ai/img_res/5d1a3f246d2517d249aad5993f766d4a.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              {t('welcome')}
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90">
              {t('description')}
            </p>
            <button 
              onClick={() => {
                const propertiesSection = document.getElementById('propiedades');
                if (propertiesSection) {
                  propertiesSection.scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                  });
                }
              }}
              className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('exploreProperties')}
            </button>
          </div>
        </div>
      </section>

      {/* Property Search */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <PropertySearch 
            onSearch={handleSearch}
            onReset={handleResetSearch}
          />
        </div>
      </section>

      {/* Compare Button */}
      {compareList.length > 0 && (
        <div className="fixed bottom-6 right-6 z-40">
          <button
            onClick={() => setShowCompare(true)}
            className="bg-blue-600 text-white px-6 py-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-scales-3-line text-lg"></i>
            {t('compare')} ({compareList.length})
          </button>
        </div>
      )}

      {/* Featured Properties */}
      <section id="propiedades" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">{t('featuredProperties')}</h2>
            <p className="text-xl text-gray-600">
              {filteredProperties.length} {t('properties')} {filteredProperties.length !== properties.length && `de ${properties.length}`}
            </p>
          </div>

          {filteredProperties.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-home-line text-4xl text-gray-400 mb-4"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay propiedades disponibles</h3>
              <p className="text-gray-500">Las propiedades aparecerán aquí una vez que sean agregadas desde el panel de administración.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProperties.map((property) => (
                <div 
                  key={property.id} 
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
                  onClick={() => handlePropertyClick(property)}
                >
                  <div className="relative h-64">
                    <img
                      src={property.images[0]}
                      alt={property.title}
                      className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                    />
                    
                    {/* Badges de disponibilidad */}
                    <div className="absolute top-4 left-4 flex flex-col gap-2">
                      {getAvailabilityBadges(property).map((badge, index) => (
                        <span key={index} className={`px-3 py-1 rounded-full text-sm font-semibold ${badge.color}`}>
                          <i className={`${badge.icon} mr-1`}></i>
                          {badge.text}
                        </span>
                      ))}
                    </div>
                    
                    {property.is_featured && (
                      <div className="absolute top-4 right-4">
                        <span className="px-3 py-1 bg-purple-600 text-white rounded-full text-sm font-semibold">
                          <i className="ri-star-line mr-1"></i>
                          Destacada
                        </span>
                      </div>
                    )}
                    
                    {/* Image Counter Indicator */}
                    {property.images.length > 1 && (
                      <div className="absolute bottom-4 right-4 bg-black bg-opacity-50 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                        <i className="ri-image-line"></i>
                        {property.images.length}
                      </div>
                    )}
                    
                    {/* Video Indicator */}
                    {property.videos && property.videos.length > 0 && (
                      <div className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
                        <i className="ri-video-line"></i>
                        {property.videos.length}
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-xl font-bold text-gray-800 line-clamp-2">{property.title}</h3>
                    </div>
                    
                    <div className="flex items-center text-gray-600 mb-4">
                      <i className="ri-map-pin-line mr-2"></i>
                      <span className="text-sm">{property.location}</span>
                    </div>

                    {/* Características de la propiedad */}
                    <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                      {property.bedrooms > 0 && (
                        <div className="flex items-center">
                          <i className="ri-hotel-bed-line mr-1"></i>
                          <span>{property.bedrooms} {t('bedrooms')}</span>
                        </div>
                      )}
                      <div className="flex items-center">
                        <i className="ri-drop-line mr-1"></i>
                        <span>{property.bathrooms} {t('bathrooms')}</span>
                      </div>
                      <div className="flex items-center">
                        <i className="ri-square-line mr-1"></i>
                        <span>{property.area} m²</span>
                      </div>
                    </div>

                    {/* Servicios incluidos para propiedades en renta */}
                    {property.available_for_rent && property.included_services && property.included_services.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center gap-1 mb-2">
                          <i className="ri-checkbox-multiple-line text-green-600 text-sm"></i>
                          <span className="text-xs font-medium text-green-700">Servicios Incluidos:</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {getServiceIcons(property.included_services).slice(0, 3).map((service, index) => (
                            <div key={index} className="flex items-center gap-1 bg-green-50 px-2 py-1 rounded-full">
                              <i className={`${service.icon} text-xs ${service.color}`}></i>
                              <span className="text-xs text-green-700">{service.label}</span>
                            </div>
                          ))}
                          {property.included_services.length > 3 && (
                            <div className="flex items-center bg-gray-100 px-2 py-1 rounded-full">
                              <span className="text-xs text-gray-600">+{property.included_services.length - 3}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Cuota de mantenimiento */}
                    {property.available_for_rent && property.has_maintenance_fee && property.maintenance_fee && (
                      <div className="mb-4 p-2 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-2">
                          <i className="ri-tools-line text-blue-600 text-sm"></i>
                          <span className="text-xs font-medium text-blue-700">
                            Mantenimiento: {formatPrice(property.maintenance_fee, property.currency)}/mes
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Precio y botones de acción */}
                    <div className="space-y-3">
                      <div className="flex-1">
                        {formatPropertyPrice(property)}
                      </div>
                      
                      {/* Botón de Ficha Técnica */}
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePDFRequest(property);
                        }}
                        className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white px-4 py-2.5 rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all text-sm font-medium whitespace-nowrap cursor-pointer shadow-md hover:shadow-lg"
                      >
                        <i className="ri-file-pdf-line mr-2"></i>
                        Ver Ficha Técnica
                      </button>

                      <div className="flex gap-2">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleBookVisit(property);
                          }}
                          className="bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer flex-1"
                        >
                          <i className="ri-calendar-line mr-1"></i>
                          Visita
                        </button>
                        
                        {/* Botones específicos según disponibilidad */}
                        {property.available_for_sale && (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePropertyRequest(property, 'compra');
                            }}
                            className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer flex-1"
                          >
                            <i className="ri-shopping-cart-line mr-1"></i>
                            Comprar
                          </button>
                        )}
                        
                        {property.available_for_rent && (
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePropertyRequest(property, 'renta');
                            }}
                            className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer flex-1"
                          >
                            <i className="ri-key-line mr-1"></i>
                            Rentar
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Property Details Modal */}
      {selectedProperty && (
        <PropertyDetails
          property={selectedProperty}
          onClose={() => setSelectedProperty(null)}
          onBookVisit={handleBookVisit}
        />
      )}

      {/* Property Request Form Modal */}
      {showRequestForm && requestProperty && (
        <PropertyRequestForm
          property={requestProperty}
          requestType={requestType}
          onClose={() => {
            setShowRequestForm(false);
            setRequestProperty(null);
          }}
        />
      )}

      {/* Property PDF Request Modal */}
      {showPDFRequest && pdfProperty && (
        <PropertyPDFRequest
          isOpen={showPDFRequest}
          onClose={() => {
            setShowPDFRequest(false);
            setPdfProperty(null);
          }}
          property={{
            id: pdfProperty.id,
            title: pdfProperty.title,
            price_sale: pdfProperty.sale_price,
            price_rent: pdfProperty.rent_price,
            location: pdfProperty.location,
            images: pdfProperty.images
          }}
        />
      )}

      {/* About Us Section */}
      {aboutUs && (
        <section id="nosotros" className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">{aboutUs.title}</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {aboutUs.description}
              </p>
            </div>

            {/* Mission & Vision */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
              <div className="bg-blue-50 rounded-xl p-8">
                <div className="w-16 h-16 bg-blue-600 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-compass-3-line text-white text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Nuestra Misión</h3>
                <p className="text-gray-600 leading-relaxed">{aboutUs.mission}</p>
              </div>

              <div className="bg-purple-50 rounded-xl p-8">
                <div className="w-16 h-16 bg-purple-600 rounded-lg flex items-center justify-center mb-6">
                  <i className="ri-eye-line text-white text-3xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Nuestra Visión</h3>
                <p className="text-gray-600 leading-relaxed">{aboutUs.vision}</p>
              </div>
            </div>

            {/* Values */}
            <div>
              <h3 className="text-2xl font-bold text-gray-800 mb-8 text-center">Nuestros Valores</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {aboutUs.values.filter(v => v.trim()).map((value, index) => (
                  <div key={index} className="flex items-start space-x-4 bg-gray-50 rounded-lg p-6">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-white"></i>
                    </div>
                    <p className="text-gray-700">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section id="contacto" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Contáctanos</h2>
            <p className="text-xl text-gray-600">
              Estamos aquí para ayudarte a encontrar la propiedad perfecta
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-semibold text-gray-800 mb-6">Información de Contacto</h3>
                
                <div className="space-y-6">
                  {/* Phone Numbers */}
                  {contactInfo && contactInfo.phones && contactInfo.phones.length > 0 && contactInfo.phones.some(p => p.trim()) && (
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-blue-600 text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Teléfonos</h4>
                        {contactInfo.phones.filter(p => p.trim()).map((phone, index) => (
                          <p key={index} className="text-gray-600">
                            <a href={`tel:${phone.replace(/\s/g, '')}`} className="hover:text-blue-600 transition-colors">
                              {phone}
                            </a>
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Email */}
                  {contactInfo && contactInfo.emails && contactInfo.emails.length > 0 && contactInfo.emails.some(e => e.trim()) && (
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-green-600 text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Email</h4>
                        {contactInfo.emails.filter(e => e.trim()).map((email, index) => (
                          <p key={index} className="text-gray-600">
                            <a href={`mailto:${email}`} className="hover:text-blue-600 transition-colors">
                              {email}
                            </a>
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Address */}
                  {contactInfo && contactInfo.address && contactInfo.address.trim() && (
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-map-pin-line text-purple-600 text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Dirección</h4>
                        <p className="text-gray-600 whitespace-pre-line">
                          {contactInfo.address}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Business Hours */}
                  {contactInfo && contactInfo.schedule && (contactInfo.schedule.weekdays || contactInfo.schedule.saturday || contactInfo.schedule.sunday) && (
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-orange-600 text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-2">Horario de Atención</h4>
                        <div className="text-gray-600 space-y-1">
                          {contactInfo.schedule.weekdays && contactInfo.schedule.weekdays.trim() && (
                            <p>
                              <span className="font-medium">Lunes - Viernes:</span> {contactInfo.schedule.weekdays}
                            </p>
                          )}
                          {contactInfo.schedule.saturday && contactInfo.schedule.saturday.trim() && (
                            <p>
                              <span className="font-medium">Sábados:</span> {contactInfo.schedule.saturday}
                            </p>
                          )}
                          {contactInfo.schedule.sunday && contactInfo.schedule.sunday.trim() && (
                            <p>
                              <span className="font-medium">Domingos:</span> {contactInfo.schedule.sunday}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Map */}
              {contactInfo && contactInfo.map_url && contactInfo.map_url.trim() && (
                <div className="bg-gray-100 rounded-lg overflow-hidden">
                  {contactInfo.map_url.includes('embed') ? (
                    <iframe
                      src={contactInfo.map_url}
                      width="100%"
                      height="300"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Ubicación de GRUPO FR"
                    ></iframe>
                  ) : (
                    <div className="p-6 text-center">
                      <div className="mb-4">
                        <i className="ri-map-pin-line text-4xl text-blue-600"></i>
                      </div>
                      <h4 className="font-semibold text-gray-800 mb-2">Ver Ubicación en Google Maps</h4>
                      <p className="text-sm text-gray-600 mb-4">
                        Haz clic en el botón para ver nuestra ubicación en Google Maps
                      </p>
                      <a
                        href={contactInfo.map_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-map-2-line mr-2"></i>
                        Abrir en Google Maps
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-xl p-8 shadow-sm">
              <h3 className="text-2xl font-semibold text-gray-800 mb-6">Envíanos un Mensaje</h3>
              
              <form 
                id="contact-form"
                data-readdy-form
                action="https://readdy.ai/api/form/d4banifhkbdsb76v73fg"
                method="POST"
                className="space-y-6"
                onSubmit={(e) => {
                  e.preventDefault();
                  const form = e.target as HTMLFormElement;
                  const formData = new FormData(form);
                  
                  // Validar campos requeridos
                  const nombre = formData.get('nombre') as string;
                  const email = formData.get('email') as string;
                  const telefono = formData.get('telefono') as string;
                  const mensaje = formData.get('mensaje') as string;
                  
                  if (!nombre?.trim()) {
                    alert('Por favor ingrese su nombre');
                    return;
                  }
                  if (!email?.trim()) {
                    alert('Por favor ingrese su email');
                    return;
                  }
                  if (!telefono?.trim()) {
                    alert('Por favor ingrese su teléfono');
                    return;
                  }
                  if (!mensaje?.trim()) {
                    alert('Por favor ingrese su mensaje');
                    return;
                  }
                  if (mensaje.length > 500) {
                    alert('El mensaje no puede exceder 500 caracteres');
                    return;
                  }
                  
                  // Enviar formulario
                  const submitData = new URLSearchParams();
                  submitData.append('nombre', nombre);
                  submitData.append('email', email);
                  submitData.append('telefono', telefono);
                  submitData.append('asunto', formData.get('asunto') as string || '');
                  submitData.append('mensaje', mensaje);
                  
                  fetch('https://readdy.ai/api/form/d4banifhkbdsb76v73fg', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/x-www-form-urlencoded',
                    },
                    body: submitData
                  })
                  .then(response => {
                    if (response.ok) {
                      alert('¡Mensaje enviado exitosamente! Nos pondremos en contacto contigo pronto.');
                      form.reset();
                    } else {
                      alert('Error al enviar el mensaje. Por favor intenta nuevamente.');
                    }
                  })
                  .catch(error => {
                    console.error('Error:', error);
                    alert('Error al enviar el mensaje. Por favor intenta nuevamente.');
                  });
                }}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre Completo *
                    </label>
                    <input
                      type="text"
                      id="nombre"
                      name="nombre"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Tu nombre completo"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="tu@email.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-2">
                      Teléfono *
                    </label>
                    <input
                      type="tel"
                      id="telefono"
                      name="telefono"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="+34 123 456 789"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="asunto" className="block text-sm font-medium text-gray-700 mb-2">
                      Asunto
                    </label>
                    <select
                      id="asunto"
                      name="asunto"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                    >
                      <option value="">Seleccionar asunto</option>
                      <option value="compra">Compra de propiedad</option>
                      <option value="venta">Venta de propiedad</option>
                      <option value="alquiler">Alquiler</option>
                      <option value="consulta">Consulta general</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="mensaje" className="block text-sm font-medium text-gray-700 mb-2">
                    Mensaje *
                  </label>
                  <textarea
                    id="mensaje"
                    name="mensaje"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-vertical"
                    placeholder="Cuéntanos en qué podemos ayudarte..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Máximo 500 caracteres</p>
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-send-plane-line mr-2"></i>
                  Enviar Mensaje
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <img 
                src="https://static.readdy.ai/image/05c782bef5e03af806ba1d51091d6c47/3aa9c4448526bbcc8377a061d058f682.png" 
                alt="GRUPO FR" 
                className="h-16 w-auto object-contain brightness-0 invert"
              />
            </div>
            
            <div className="text-center md:text-right">
              <p className="text-gray-400">
                © 2024 GRUPO FR. {t('allRights')}.
              </p>
              <div className="flex items-center justify-center md:justify-end gap-4 text-gray-400 text-sm mt-1">
                <a 
                  href="https://readdy.ai/?origin=logo" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="hover:text-white transition-colors"
                >
                  Website Builder
                </a>
                <span className="text-gray-600">|</span>
                <a 
                  href="/admin" 
                  className="hover:text-white transition-colors"
                >
                  {t('adminPanel')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Compare Modal */}
      {showCompare && (
        <PropertyCompare
          properties={compareList}
          onRemoveFromCompare={removeFromCompare}
          onClose={() => setShowCompare(false)}
        />
      )}

      {/* Appointment Booking Modal */}
      {showAppointmentBooking && appointmentProperty && (
        <AppointmentBooking
          property={appointmentProperty}
          onClose={() => {
            setShowAppointmentBooking(false);
            setAppointmentProperty(null);
          }}
        />
      )}
    </div>
  );
}